import React, { memo } from 'react';
import Div from '@/shared/Div';
import Container from '@/shared/Container';
import Heading from '@/shared/Heading';
import Paragraph from '@/shared/Paragraph';
import Button from '@/shared/Button';
import Map from '@/components/Map';
import withContentFlags from '@/hoc/withContentFlags';
import ArrowForward from '@/components/Icons/ArrowForward';

interface Article {
  link: string;
  title: string;
  subtitle: string;
}

export interface SubsidiaryFinancialsProps {
  title: string;
  data: {
    cta: {
      id: number;
      href: string | null;
      size: string;
      label: string;
      theme: string;
      target: string;
    };
    articles: Article[];
  };
}

const SubsidiaryFinancials = (props: SubsidiaryFinancialsProps) => {
  const { title, data } = props;
  const { cta, articles = [] } = data || {};

  const renderArticle = (article: Article, index: number) => {
    const { link, title: articleTitle, subtitle } = article;

    return (
      <Div
        key={index}
        className="flex flex-col items-start p-6 bg-white rounded-2xl border border-gray-200 w-full min-w-[calc(100%-3rem)] md:min-w-0"
      >
        {/* Article Title */}
        <Heading
          level="3"
          className="text-hd-3 text-cti-primary mb-4 pb-4 border-b border-gray-300 w-full"
        >
          {articleTitle}
        </Heading>

        {/* View More Link */}
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-between w-full text-body-base transition-colors"
        >
          <Paragraph className="text-body-lg ">{subtitle}</Paragraph>
          <ArrowForward className="[&_path]:fill-cm-brand hover:[&_path]:fill-cm-primary [&_path]:transition-colors" />
        </a>
      </Div>
    );
  };

  return (
    <Div className="bg-white py-7 md:py-20">
      <Container className="flex flex-col justify-center items-center gap-s7">
        {/* Header Section with Title and CTA */}
        <Div className="flex justify-between items-center self-stretch">
          <Heading level="1" className="text-hd-1 md:text-desktop-h-1 text-cm-brand">
            {title}
          </Heading>

          {/* CTA Button - Hidden on Mobile, Visible on Desktop */}
          {cta && (
            <Button
              label={cta.label}
              href={cta.href || undefined}
              target={cta.target}
              theme={cta.theme as any}
              size={cta.size as any}
              dataTestId="subsidiary-financials-cta"
              rounded
              className="hidden md:flex"
            >
              {cta.label}
            </Button>
          )}
        </Div>

        {/* Articles Grid */}
        <Div className="flex overflow-x-auto gap-s6 md:grid md:grid-cols-2 lg:grid-cols-4 self-stretch">
          <Map list={articles} render={renderArticle} />
        </Div>

        {/* CTA Button - Visible on Mobile Only, Below Articles */}
        {cta && (
          <Button
            label={cta.label}
            href={cta.href || undefined}
            target={cta.target}
            theme={cta.theme as any}
            size={cta.size as any}
            dataTestId="subsidiary-financials-cta-mobile"
            rounded
            className="md:hidden self-start"
          >
            {cta.label}
          </Button>
        )}
      </Container>
    </Div>
  );
};

export default withContentFlags(memo(SubsidiaryFinancials));
